import {
  ButtonType,
  DialogFooterButtonsWidget,
  DialogPositioningBehavior,
  DialogType,
  DialogWidget,
  TextFieldWidget,
} from '@tableau/widgets';
import React from 'react';

import { CreateCustomDialogFn } from '~commonInfra/dialogs/ReactModalService';
import { ToasterService, ToastType } from '~commonInfra/services/ToasterService';
import { getAppStore } from '~src/bootstrap/redux/store/AppStore';
import { Messages } from '~src/codegen/Localize';
import { requestSpaceContents } from '~src/spacesPlatform/actions/RequestSpaceContentsActions';

import { renameRelayPlan } from '../utils/relayPlanActions';
import { RelayPlanDialogStyles } from './RelayPlanDialogStyles';

export interface RenameRelayPlanDialogProps {
  luid: string;
  currentName: string;
  onClose: () => void;
  /** Called with the new name after a successful rename (e.g. so a detail page can re-read the plan). */
  onRenamed?: (name: string) => void;
}

/** Small dialog to rename a Content Relay plan (name-only update via the content-mobility API). */
export function RenameRelayPlanDialog(props: RenameRelayPlanDialogProps): JSX.Element {
  const [name, setName] = React.useState(props.currentName);
  const [submitting, setSubmitting] = React.useState(false);

  const trimmed = name.trim();
  const canSubmit = !submitting && !!trimmed && trimmed !== props.currentName;

  const onSubmit = function(): void {
    if (!canSubmit) {
      return;
    }
    setSubmitting(true);
    renameRelayPlan(props.luid, trimmed)
      .then(function() {
        props.onClose();
        getAppStore().dispatch(requestSpaceContents());
        if (props.onRenamed) {
          props.onRenamed(trimmed);
        }
        ToasterService.instance.pop({ type: ToastType.Success, text: Messages.relayPlanRenamedToast({ PLAN_NAME: trimmed }) });
      })
      .catch(function() {
        setSubmitting(false);
        ToasterService.instance.pop({ type: ToastType.Error, text: Messages.relayPlanErrorToast() });
      });
  };

  const footer = (
    <DialogFooterButtonsWidget
      buttons={[
        {
          buttonType: ButtonType.Outline,
          onClick: props.onClose,
          children: Messages.relayPlanDialogCancel(),
          testId: 'relay-plan-rename-cancel',
        },
        {
          buttonType: ButtonType.Primary,
          onClick: onSubmit,
          disabled: !canSubmit,
          children: Messages.rename(),
          testId: 'relay-plan-rename-confirm',
        },
      ]}
    />
  );

  return (
    <DialogWidget
      type={DialogType.NormalWithTitleBar}
      title={Messages.relayPlanRenameTitle()}
      showCloseButton={true}
      onCloseRequest={props.onClose}
      positioningBehavior={DialogPositioningBehavior.Centered}
      isModal={true}
      dialogWidth={440}
      footerContent={footer}
      testId={'relay-plan-rename-dialog'}
    >
      <TextFieldWidget
        className={RelayPlanDialogStyles.fullWidthField}
        label={Messages.relayPlanRenameLabel()}
        value={name}
        onChange={function(e) { setName(e.target.value); }}
        testId={'relay-plan-rename-name'}
      />
    </DialogWidget>
  );
}

/** Options for ReactModalService.openCustom. */
export function RenameRelayPlanDialogOpts(luid: string, currentName: string, onRenamed?: (name: string) => void): CreateCustomDialogFn {
  return function(closeAndDismiss) {
    return <RenameRelayPlanDialog luid={luid} currentName={currentName} onClose={closeAndDismiss.close} onRenamed={onRenamed} />;
  };
}
